import { cn } from '@/lib/utils';

/**
 * Admin sign-in chrome, matching the regara-ops login screen: a deep-navy page
 * with two soft brand glows and one frosted-glass card holding the logo, the
 * page title and the sign-in control. Admin-only — the customer /login keeps
 * its own AuthShell.
 */
export function AdminAuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-navy-deep px-4">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-electric/10 blur-[130px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 right-0 h-[360px] w-[360px] translate-x-1/4 rounded-full bg-navy-mid/50 blur-[130px]"
      />
      <div className="relative w-full max-w-[400px] rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center shadow-2xl backdrop-blur-sm sm:p-10">
        <img src="/regara_logo_1.png" alt="REGARA" className="mx-auto h-12 w-auto" />
        <h1 className="mt-7 text-lg font-semibold tracking-tight text-white">Admin Console</h1>
        <div className="mt-8">{children}</div>
      </div>
    </div>
  );
}

export type AdminAuthAlertState = { kind: 'error' | 'info'; text: string } | null;

export function AdminAuthAlert({ alert }: { alert: AdminAuthAlertState }) {
  if (!alert) return null;
  return (
    <p
      role="alert"
      className={cn(
        'mt-4 text-sm',
        alert.kind === 'error' ? 'text-state-error-border' : 'text-state-info-border',
      )}
    >
      {alert.text}
    </p>
  );
}

export function AdminAuthField({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4 text-left">
      <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-silver">
        {label}
      </label>
      {children}
      {error && <p className="mt-1.5 text-[13px] text-state-error-border">{error}</p>}
    </div>
  );
}

export const AdminAuthInput = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    {...props}
    className={cn(
      'w-full rounded-lg border border-white/15 bg-white/[0.06] px-3.5 py-2.5 text-[15px] text-white',
      'placeholder:text-white/40 focus:border-electric focus:outline-none focus:ring-2 focus:ring-electric/30',
      props.className,
    )}
  />
);

export function AdminAuthButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={cn(
        'w-full cursor-pointer rounded-lg border-0 bg-electric px-4 py-2.5 text-[15px] font-semibold text-navy-deep',
        'hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60',
        props.className,
      )}
    />
  );
}

/** A quiet text link under the card's main control (e.g. "Emergency sign-in"). */
export function AdminAuthLink(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...props}
      className={cn(
        'cursor-pointer border-0 bg-transparent p-0 text-sm text-white/60 underline hover:text-white',
        props.className,
      )}
    />
  );
}
